import React from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { CrudPage } from "../components/CrudPage";
import { FundSelectionCard } from "../components/funds/FundSelectionCard";
import { tadbir } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { TadbirFund, useFunds } from "../hooks/useFunds";
import {
  formatFundCurrency,
  formatFundNumber,
} from "../components/funds/fundCardUtils";
import {
  getUserIdentifier,
  getUserIdentifierLabel,
} from "../utils/userIdentity";

const FIELD_LABELS: Record<string, string> = {
  // groupName: "نام گروه",
  count: "تعداد",
  currentNAVValue: "ارزش NAV جاری",
  totalUnitValue: "ارزش کل واحدها",
  totalSellValue: "ارزش کل فروش",
  remainingUnits: "واحد باقی‌مانده",
  totalRemainingValue: "ارزش مانده",
};

const COLUMN_ORDER = [
  // "groupName",
  "count",
  "currentNAVValue",
  "totalUnitValue",
  "totalSellValue",
  "remainingUnits",
  "totalRemainingValue",
];

const getRowsFromCardex = (cardex: any) => {
  if (Array.isArray(cardex)) return cardex;
  if (!cardex || typeof cardex !== "object") return [];

  const arrayEntry = Object.values(cardex).find((value) =>
    Array.isArray(value),
  );
  return Array.isArray(arrayEntry) ? arrayEntry : [];
};

const getNumericValue = (value: unknown) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatCellValue = (key: string, value: unknown) => {
  if (value === null || value === undefined || value === "") return "—";

  if (key === "groupName") {
    return String(value);
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  if (
    [
      "currentNAVValue",
      "totalUnitValue",
      "totalSellValue",
      "totalRemainingValue",
    ].includes(key)
  ) {
    return `${formatFundCurrency(numericValue)} ریال`;
  }

  return formatFundNumber(numericValue);
};

const columns = COLUMN_ORDER.map((key) => ({
  header: FIELD_LABELS[key],
  accessor: key,
  render: (item: any) => formatCellValue(key, item?.[key]),
}));

const findFundBySeo = (funds: TadbirFund[], selectedFund: string) =>
  funds.find(
    (fund) =>
      String(fund.nav?.seoRegisterNo || fund.fundRegisterCode) === selectedFund,
  );

export const InvestCardexPage: React.FC = () => {
  const { user } = useAuth();
  const identifier = getUserIdentifier(user);
  const identifierLabel = getUserIdentifierLabel(user);
  const { data: fundsData, isLoading: isFundsLoading } = useFunds();
  const funds = React.useMemo(() => fundsData?.items || [], [fundsData]);
  const [selectedFund, setSelectedFund] = React.useState("");
  const [rows, setRows] = React.useState<any[]>([]);
  const cardBg = useColorModeValue("white", "dark.50");
  const mutedColor = useColorModeValue("gray.500", "gray.400");

  React.useEffect(() => {
    if (!selectedFund && funds.length > 0) {
      const firstFund = funds[0];
      setSelectedFund(
        String(firstFund.nav?.seoRegisterNo || firstFund.fundRegisterCode),
      );
    }
  }, [funds, selectedFund]);

  if (!identifier) {
    return (
      <Alert status="warning" borderRadius="lg">
        <AlertIcon />
        {identifierLabel} کاربر برای دریافت کاردکس سرمایه‌گذار یافت نشد.
      </Alert>
    );
  }

  if (isFundsLoading) {
    return (
      <VStack
        w="100%"
        h="100dvh"
        py={10}
        spacing={4}
        align="center"
        justify="center"
      >
        <Spinner size="xl" color="brand.500" />
        <Text color={mutedColor}>در حال دریافت صندوق‌ها...</Text>
      </VStack>
    );
  }

  const selectedFundData = findFundBySeo(funds, selectedFund);
  const totalBuyValue = rows.reduce(
    (sum, item) => sum + getNumericValue(item?.totalUnitValue),
    0,
  );
  const totalSellValue = rows.reduce(
    (sum, item) => sum + getNumericValue(item?.totalSellValue),
    0,
  );

  const quickAccess = (
    <VStack align="stretch" spacing={5}>
      <Box borderRadius="3xl" bg={cardBg}>
        <VStack align="stretch" spacing={5}>
          <HStack
            justify="space-between"
            align={{ base: "start", md: "center" }}
            flexWrap="wrap"
            spacing={3}
          >
            <VStack align="start" spacing={1}>
              <Heading size="md">دسترسی سریع صندوق‌ها</Heading>
              <Text color={mutedColor} fontSize="sm">
                صندوق فعال را تغییر دهید و کاردکس همان صندوق را ببینید
              </Text>
            </VStack>

            <HStack spacing={3} flexWrap="wrap">
              <Badge
                colorScheme="green"
                variant="subtle"
                px={3}
                py={2}
                borderRadius="full"
                fontSize="sm"
              >
                جمع ارزش خرید: {formatFundCurrency(totalBuyValue)} ریال
              </Badge>
              <Badge
                colorScheme="red"
                variant="subtle"
                px={3}
                py={2}
                borderRadius="full"
                fontSize="sm"
              >
                جمع ارزش فروش: {formatFundCurrency(totalSellValue)} ریال
              </Badge>
            </HStack>
          </HStack>

          <FundSelectionCard
            fundData={funds}
            value={selectedFund}
            onChange={setSelectedFund}
          />
        </VStack>
      </Box>
    </VStack>
  );

  return (
    <VStack align="stretch" spacing={5} w="100%" h="100%">
      <CrudPage
        title={
          selectedFundData
            ? `کاردکس سرمایه‌گذاری ${selectedFundData.fundName.trim()}`
            : "کاردکس سرمایه‌گذاری"
        }
        columns={columns}
        queryKey="investCardex"
        dataKey="investCardexRows"
        resourceName="investCardex"
        api={{
          getAll: async () => {
            const response = await tadbir.getInvestCardex(selectedFund);
            const rows = getRowsFromCardex(response.investCardex);

            return {
              investCardexRows: rows,
              totalData: rows.length,
              currentPage: 1,
              totalPage: 1,
            };
          },
        }}
        FormComponent={() => null}
        showAddButton={false}
        enableEdit={false}
        enableDelete={false}
        enableView={false}
        pageSize={1}
        queryDependencies={[selectedFund]}
        queryEnabled={!!selectedFund && !!identifier}
        keepPreviousData={false}
        beforeTable={quickAccess}
        errorMessage="دریافت کاردکس سرمایه‌گذار با خطا مواجه شد."
        onDataLoaded={(queryData) => setRows(queryData?.investCardexRows || [])}
      />
    </VStack>
  );
};
